import axios from 'axios';

const root = '/api/credit/abf/forward-flow';

export type ProgramWorkspace = 'overview' | 'agreement' | 'offers' | 'assets' | 'collections' | 'monitoring'
  | 'reporting' | 'controls' | 'activity';

/** The server's single next step: what, why, who, and where it is done. */
export type ProgramAction = {
  key: string; label: string; reason: string; role: 'MAKER' | 'CHECKER' | null;
  workspace: ProgramWorkspace; query: string | null; actorMayAct: boolean; whyNot: string | null;
};
export type JourneyStep = {key: string; label: string; done: boolean; current: boolean; detail: string};

export type Offer = {
  deliveryId: number; sellerReference: string; revisionId: number; revisionNumber: number; sourceLane: string;
  cutoffDate: string; receivedAt: string; receivedBy: string; statedCount: number; statedUpb: number;
  normalizedCount: number; normalizedUpb: number; revisionState: string; batchId: number | null; batchState: string | null;
  evaluationId: number | null; evaluationState: string | null; proposedCount: number | null; proposedUpb: number | null;
  currentWire: number | null; decisionId: number | null; decisionState: string | null; preparedBy: string | null;
  approvedBy: string | null; totalLoans?: number; reservedLoans?: number; stagedLoans?: number; preclearLoans?: number;
  clearedLoans?: number; postedLoans?: number; refusedLoans?: number; failedLoans?: number; totalUpb?: number; postedUpb?: number;
  settlementGroups?: number; settledGroups?: number; expectedWire?: number; confirmedWire?: number;
  steps: JourneyStep[]; open: boolean; stage: string; nextAction: ProgramAction; route: string;
};

export type Approval = {
  key: string; kind: string; programId: number; programName: string; action: string; detail: string;
  preparedBy: string | null; since: string | null; workspace: ProgramWorkspace; query: string | null; route: string;
  actorMayAct: boolean; whyNot: string | null;
};

export type AttentionItem = {
  key: string; tone: 'critical' | 'warning' | 'progress'; category: string; text: string; detail: string;
  dueDate: string | null; nextAction: ProgramAction;
};

export type ProgramFacts = {
  agreement: {versionNumber?: number; effectiveFrom?: string; seller?: string; servicer?: string; pricePer100?: string};
  capacity: {known: boolean; closed?: boolean; limit?: number; monthlyLimit?: number; remaining?: number; monthlyRemaining?: number;
    purchased?: number; committed?: number; asOf?: string; reason?: string};
  owned: {loans: number; upb: number};
  owedToSeller: {payable: number; holdback: number; unpaid: number};
  offers: {open: number; total: number; nextPurchaseDate?: string};
  servicing: {nextServicingDue?: string; periodId?: number; periodLabel?: string; nextStep?: string; openItems?: number};
  claims: {open: number; sellerOwes: number};
};

export type ProgramOverview = {
  programId: number; programCode: string; programName: string; portfolioName: string; status: string;
  statusLabel: string; businessDate: string; purchaseEnabled: boolean; purchaseGate: {open: boolean; reason: string | null};
  facts: ProgramFacts; journey: JourneyStep[]; offers: Offer[]; approvals: Approval[]; attention: AttentionItem[];
  nextAction: ProgramAction; closure: Record<string, number | string>; generatedAt: string;
};

export type ProgramCard = {
  id: number; programCode: string; programName: string; portfolioName: string; status: string; statusLabel: string;
  facts?: ProgramFacts; nextAction: ProgramAction; approvals?: number; attention?: number;
};

export type StateChange = {
  id: number; from_status: string; to_status: string; fromLabel: string; toLabel: string; reason: string;
  source_ref?: string | null; prepared_by: string; prepared_at: string; verified_by?: string | null;
  verified_at?: string | null; state: 'DRAFT' | 'APPLIED' | 'VOID'; actorMayApply: boolean; whyNot: string | null;
};

export type BulkResult = {
  step: string; attempted: number; completed: number;
  refused: Array<{childId: number; sourceLoanId: string; reason: string}>;
};

export const overviewApi = {
  directory: async () => (await axios.get<ProgramCard[]>(`${root}/directory`)).data,
  approvals: async () => (await axios.get<Approval[]>(`${root}/approvals`)).data,
  overview: async (program: number) => (await axios.get<ProgramOverview>(`${root}/programs/${program}/overview`)).data,
  offers: async (program: number) => (await axios.get<Offer[]>(`${root}/programs/${program}/offers`)).data,
  stateChanges: async (program: number) => (await axios.get<StateChange[]>(`${root}/programs/${program}/state-changes`)).data,
  transferAll: async (program: number, decision: number, body: {transferDate: string; expectedCashDate: string;
      evidenceRef: string; evidenceSha256: string}) =>
    (await axios.post<BulkResult>(`${root}/programs/${program}/decisions/${decision}/loans/transfer`, body)).data,
  // Checker approves-and-books every transferred loan in one action (approve pre-clear -> send -> execute -> post).
  approveAll: async (program: number, decision: number) =>
    (await axios.post<BulkResult>(`${root}/programs/${program}/decisions/${decision}/loans/approve`)).data,
};
